import {
  type EvaluationDetails,
  type IConfigCatLogger,
  type IOverrideDataSource,
  OverrideBehaviour,
  PollingMode,
  type Setting,
  type SettingValue,
  User,
  deserializeConfig,
  getClient,
} from '@configcat/sdk'

export type { SettingValue }

export type FlagMap = Readonly<Record<string, SettingValue>>
export type FlagEvalResult = { success: true; flags: FlagMap } | { success: false; error: unknown }

export type FlagUser = {
  id?: string
  email?: string
  tenant?: string
  roles?: string
  tags?: string[]
}

export type GetFlagsOptions = {
  onFlagEvaluated?: (details: EvaluationDetails) => void
  logger?: IConfigCatLogger
}

const DEVICE_ID_STORAGE_KEY = 'configcat-device-id'
const PLACEHOLDER_EMAIL = 'placeholder'

// not all browsers support localStorage, so we use a local in-memory fallback
let cachedDeviceId: string | undefined

export function getFlags(rules: string, user?: FlagUser, options?: GetFlagsOptions): FlagEvalResult {
  // Intentionally return an empty flags object if rules are empty
  if (!rules) return { success: true, flags: Object.freeze({}) }

  try {
    // The SDK key argument is the rules string on purpose. For LocalOnly mode, `rules` is a good unique identifier.
    const client = getClient(rules, PollingMode.ManualPoll, {
      logger: options?.logger,
      setupHooks: options?.onFlagEvaluated ? (hooks) => hooks.on('flagEvaluated', options.onFlagEvaluated!) : undefined,
      flagOverrides: {
        // LocalOnly evaluates this data source instead of the CDN. https://configcat.com/docs/sdk-reference/js/overview/#custom-data-source-implementation
        dataSource: new EmbeddedConfigJsonDataSource(rules),
        behaviour: OverrideBehaviour.LocalOnly,
      },
    })

    const snapshot = client.snapshot()
    const flagMap: Record<string, SettingValue> = {}

    for (const key of snapshot.getAllKeys()) {
      flagMap[key] = snapshot.getValue(key, undefined, toSdkUser(user))
    }

    return { success: true, flags: Object.freeze(flagMap) }
  } catch (error) {
    return { success: false, error }
  }
}

function toSdkUser(user: FlagUser | undefined): User {
  const resolved = user ?? {}
  const id = resolved.id ?? ''
  const deviceId = getOrCreateDeviceId()

  return new User(id, resolved.email ?? PLACEHOLDER_EMAIL, '', {
    tenant: resolved.tenant ?? '',
    roles: resolved.roles ?? '',
    tags: resolved.tags ?? [],
    random_device_id: deviceId,
    rollout_id: id || deviceId,
  })
}

function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY)

    if (existing) {
      cachedDeviceId = existing

      return existing
    }
  } catch {
    // Private mode / missing localStorage — fall through to the module singleton.
  }

  if (!cachedDeviceId) {
    cachedDeviceId = generateDeviceId()
  }

  try {
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, cachedDeviceId)
  } catch {
    // Ignore persist failure (quota / Android webview). The module let keeps this id for the session.
  }

  return cachedDeviceId
}

function generateDeviceId(): string {
  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  /* v8 ignore next - crypto.randomUUID missing in some webviews -- @preserve */
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0
    const value = char === 'x' ? random : (random & 0x3) | 0x8

    return value.toString(16)
  })
}

/**
 * Serves the already-fetched config_v6 JSON as LocalOnly overrides.
 * Same class as ConfigCat's custom data source sample: https://configcat.com/docs/sdk-reference/js/overview/#custom-data-source-implementation
 */
class EmbeddedConfigJsonDataSource implements IOverrideDataSource {
  private settings: Record<string, Setting>

  constructor(configJson: string) {
    this.settings = deserializeConfig(configJson).f ?? {}
  }

  getOverrides(): Record<string, Setting> {
    return this.settings
  }
}
